import * as React from 'react'
import Select from 'react-select'
import { ISimpleDriverDTO, IActiveTripResponseV2, IDriverSearchObject, ITripEdit } from '../../../types'
import UIMessages from '../../../utils/ui-messages'
import Modal from '../../ui/Modal'
import { RouteComponentProps } from 'react-router-dom'
import { MoonLoader } from 'react-spinners'
import history from '../../../history'
import { TripStatuses } from 'src/constants/trip-statuses'

export interface IAssignDriverModalProps extends RouteComponentProps {
  isFetching: boolean
  currentOrganizationId: string
  operators: IDriverSearchObject[]
  tripId: string
  trip: IActiveTripResponseV2
  onClose: () => void
  fetchOperators: (organizationId: string) => Promise<any>
  getTrip: (tripId: string) => Promise<any>
  assignOperator: (trip: IActiveTripResponseV2, operator: ISimpleDriverDTO, isOndemandTrip: boolean) => Promise<any>
  saveData: (params: ITripEdit) => Promise<any>
  assignScheduledGroupRideOperator: (id: string, operator: ISimpleDriverDTO, tripId?: string) => Promise<any>
  unAssignScheduledGroupRideOperator: (id: string) => Promise<any>
}

interface IAssignDriverModalState {
  isSubmitting: boolean
  error: string
  selected: any
  Operator?: string | undefined
}

class AssignDriverModal extends React.Component<IAssignDriverModalProps, IAssignDriverModalState> {
  constructor(props: IAssignDriverModalProps) {
    super(props)

    this.state = {
      isSubmitting: false,
      error: '',
      selected: { label: '', value: '' },
      Operator: ''
    }
  }

  public componentDidMount() {
    this.props.fetchOperators(this.props.currentOrganizationId)
    if (!this.props.trip) {
      this.props.getTrip(this.props.tripId)
    }
  }

  public componentWillUnmount(): void {
    this.setState({
      error: ''
    })
  }

  public render() {
    const operatorOptions = this.props.operators.map(op => {
      return { label: op.name, value: op.id }
    })

    const isOndemandTrip =
      this.props.trip &&
      [
        TripStatuses.REQUESTED,
        TripStatuses.WAITING,
        TripStatuses.DISPATCH_ASSIGNED_DRIVER,
        TripStatuses.PENDING
      ].includes(this.props.trip.lastStatus)

    const title = isOndemandTrip ? 'Press to Cancel and Assign trip to Driver' : ''
    return (
      <>
        {operatorOptions.length ? (
          <Modal>
            <div className='fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center'>
              <div className='bg-white col-3 p2'>
                <span className='ModalAddTitle font-small weight-300 font-grey2 ml2 mr2 uppercase'>
                  {UIMessages.trip.assign}
                </span>
                <div
                  style={{
                    pointerEvents: this.state.isSubmitting ? 'none' : 'auto',
                    opacity: this.state.isSubmitting ? 0.4 : 1.0
                  }}
                  className='ml2 mr2 mt1'
                >
                  <div className='mb1 mt1'>
                    <span className='font-grey3 h5'>Operators</span>
                    <Select
                      name='SelectOperator'
                      clearable={false}
                      value={this.state.selected}
                      onChange={this.onChange}
                      options={operatorOptions}
                    />
                  </div>
                  <div>{this.state.error ? <p className='font-red m0'>{this.state.error}</p> : null}</div>
                </div>
                {this.props.isFetching === true ? (
                  <MoonLoader sizeUnit={'px'} size={20} color={'#405264'} loading={true} />
                ) : (
                  <div className='flex items-center justify-end mt3'>
                    <button
                      type='button'
                      onClick={this.onClearDriver}
                      className='btn bg-white rounded font-light-red mr2'
                    >
                      Unassign
                    </button>
                    <button
                      type='button'
                      onClick={this.handleCancelClick}
                      disabled={this.state.isSubmitting}
                      className='CancelButton btn bg-white rounded font-light-grey mr2'
                    >
                      {UIMessages.common.cancel}
                    </button>
                    <div title={title} className='ml1 cursor--pointer'>
                      <button
                        type='button'
                        onClick={this.onAssignDriver}
                        disabled={this.state.isSubmitting}
                        className='SubmitButton btn bg-green rounded font-white weight-300'
                      >
                        {UIMessages.trip.assign}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Modal>
        ) : (
          <Modal>
            <div className='fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center'>
              <div className='bg-white col-3 waiting-modal'>
                <h4 className='mb0'>Busy at the moment</h4>
                <h5 className='mt1'>You can see your predicted wait time below</h5>
                <div className='waiting-modal-content'>
                  <h3>Status</h3>
                  <p>Searching</p>
                </div>
                <div className='waiting-modal-content'>
                  <h3>Predicted arrival</h3>
                  <p>20 to 30 mins</p>
                </div>
                <button
                  type='button'
                  onClick={this.handleCancelClick}
                  disabled={this.state.isSubmitting}
                  className='CancelButton btn bg-white rounded font-light-grey mr2'
                >
                  Ok
                </button>
              </div>
            </div>
          </Modal>
        )}
      </>
    )
  }

  private onChange = (option: any) => {
    this.setState({
      selected: option,
      error: ''
    })
  }

  private onClearDriver = async () => {
    const { operator } = this.props.trip

    const params: ITripEdit = {
      prevOperator: operator ? operator.firstName + ' ' + operator.lastName : '',
      operator: 'unassigned',
      tripId: this.props.tripId
    }

    await this.props.saveData(params).then(() => {
      if (this.props.trip.groupRideId) {
        this.props.unAssignScheduledGroupRideOperator(this.props.trip.groupRideId)
      }
    })

    this.props.getTrip(this.props.tripId)
    this.handleCancelClick()
  }

  private onAssignDriver = async () => {
    if (!this.state.selected) {
      return
    }

    this.setState({ isSubmitting: true })
    const operator = this.props.operators.find(op => op.id === this.state.selected.value)

    if (!operator) {
      return
    }

    // This block executes only if the trip has a groupRideId. Otherwise, the code will proceed to the else block
    if (this.props.trip.groupRideId) {
      // Attempt to assign the operator
      const result = await this.props.assignScheduledGroupRideOperator(
        this.props.trip.groupRideId,
        {
          id: operator.id,
          operatorId: operator.id,
          firstName: operator.firstName,
          lastName: operator.lastName,
          phone: operator.phone
        },
        this.props.tripId
      )

      // Check if the result returns an error
      if (result && result.toLowerCase().includes('error')) {
        // If there's an error, set the state with the error
        this.setState({
          isSubmitting: false,
          error: result,
          Operator: operator.firstName + ' ' + operator.lastName
        })
      } else {
        // If no error or empty/undefined result, close the modal
        this.props.getTrip(this.props.tripId)
        this.handleCancelClick()
      }
    } else {
      const operatorPayload = {
        id: operator.id,
        operatorId: operator.id,
        firstName: operator.firstName,
        lastName: operator.lastName,
        phone: operator.phone
      }
      const isOndemandTrip = [
        TripStatuses.REQUESTED,
        TripStatuses.WAITING,
        TripStatuses.DISPATCH_ASSIGNED_DRIVER
      ].includes(this.props.trip.lastStatus)
      const result = await this.props.assignOperator(this.props.trip, operatorPayload, isOndemandTrip)
      if (result && result.status && result.status >= 400) {
        this.setState({
          isSubmitting: false,
          error: result.data,
          Operator: operator.firstName + ' ' + operator.lastName
        })
      } else if (isOndemandTrip === true) {
        history.push(`/trips/${this.props.trip.id}`)
      } else {
        this.props.getTrip(this.props.tripId)
        this.handleCancelClick()
      }
    }

    //Audit Trail
    const params: ITripEdit = {
      prevOperator: this.props.trip.operator
        ? this.props.trip.operator.firstName + ' ' + this.props.trip.operator.lastName
        : 'no operator assigned',
      operator: operator.firstName + ' ' + operator.lastName
    }

    // This is for saving the data and push notifications
    await this.props.saveData(params)
  }

  private handleCancelClick = () => {
    this.setState({
      error: ''
    })
    this.props.onClose()
  }
}

export default AssignDriverModal



// WEBPACK FOOTER //
// ./src/components/trips/management/AssignOperatorModal.tsx